import axios from "axios";
import { Base_URL } from "../url";

/**
 * =========================
 * HR ORG DOCUMENT APIs
 * =========================
 */

// Upload HR Org Document
export const uploadHrOrgDocument = async (formData) => {
  try {
    const response = await axios.post(
      `${Base_URL}/HrOrgDocumentMaster/UploadDocument`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error uploading HR Org document:", error);
    throw error;
  }
};

// Update HR Org Document (metadata or new version with file)
export const updateHrOrgDocument = async (documentId, formData) => {
  try {
    const response = await axios.put(
      `${Base_URL}/HrOrgDocumentMaster/UpdateDocument/${documentId}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error(
      `Error updating HR Org document with ID ${documentId}:`,
      error,
    );
    throw error;
  }
};

export const downloadHrOrgDocument = async (documentId) => {
  const response = await axios.get(
    `${Base_URL}/HrOrgDocumentMaster/Download/${documentId}`,
    { responseType: "blob" },
  );

  const blob = new Blob([response.data], {
    type: response.headers["content-type"],
  });

  const url = window.URL.createObjectURL(blob);

  // Extract filename
  const disposition = response.headers["content-disposition"];
  let fileName = "document";

  if (disposition) {
    const match = disposition.match(/filename="(.+)"/);
    if (match?.[1]) fileName = match[1];
  }

  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();

  link.remove();
  window.URL.revokeObjectURL(url);
};

export const viewHrOrgDocument = (documentId) => {
  const url = `${Base_URL}/HrOrgDocumentMaster/View/${documentId}`;
  window.open(url, "_blank", "noopener,noreferrer");
};

// Fetch all HR Org Documents by OrgId
export const getHrOrgDocumentsByOrgId = async (orgId) => {
  try {
    const response = await axios.get(
      `${Base_URL}/HrOrgDocumentMaster/GetByOrg/${orgId}`,
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching HR Org documents for OrgId ${orgId}:`, error);
    throw error;
  }
};

// Delete HR Org Document
export const deleteHrOrgDocument = async (documentId) => {
  try {
    const response = await axios.delete(
      `${Base_URL}/HrOrgDocumentMaster/Delete/${documentId}`,
    );
    return response.data;
  } catch (error) {
    console.error(
      `Error deleting HR Org document with ID ${documentId}:`,
      error,
    );
    throw error;
  }
};

/**
 * =========================
 * HR DOCUMENT TYPE APIs
 * =========================
 */

// Add HR Document Type
export const addHrDocumentType = async (payload) => {
  try {
    const response = await axios.post(
      `${Base_URL}/DocumentTypeForHr/Add`,
      payload,
    );
    return response.data;
  } catch (error) {
    console.error("Error adding HR document type:", error);
    throw error;
  }
};

// Get ALL HR Document Types
export const getAllHrDocumentTypes = async () => {
  try {
    const response = await axios.get(`${Base_URL}/DocumentTypeForHr/GetAll`);
    return response.data;
  } catch (error) {
    console.error("Error fetching all HR document types:", error);
    throw error;
  }
};

// Get HR Document Types by OrgId
export const getHrDocumentTypesByOrgId = async (orgId) => {
  try {
    const response = await axios.get(
      `${Base_URL}/DocumentTypeForHr/GetByOrg/${orgId}`,
    );
    return response.data;
  } catch (error) {
    console.error(
      `Error fetching HR document types for OrgId ${orgId}:`,
      error,
    );
    throw error;
  }
};

// =========================
// HR DOCUMENT TYPE APIs
// =========================

// Get document type by ID
export const getHrDocumentTypeById = async (documentTypeId) => {
  try {
    const response = await axios.get(
      `${Base_URL}/DocumentTypeForHr/GetById/${documentTypeId}`,
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching document type ${documentTypeId}:`, error);
    throw error;
  }
};

/**
 * =========================
 * HR ORG DOCUMENT VISIBILITY APIs
 * =========================
 */

// Save / Update visibility
export const saveHrOrgDocumentVisibility = async (payload) => {
  try {
    const response = await axios.post(
      `${Base_URL}/HrOrgDocumentVisibility/Save`,
      payload,
    );
    return response.data;
  } catch (error) {
    console.error("Error saving document visibility:", error);
    throw error;
  }
};

// Get visibility by document
export const getVisibilityByDocumentId = async (documentId) => {
  try {
    const response = await axios.get(
      `${Base_URL}/HrOrgDocumentVisibility/GetByDocument/${documentId}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching document visibility:", error);
    throw error;
  }
};

/**
 * Get ONLY visible documents for logged-in user
 */
export const getVisibleHrOrgDocuments = async (
  orgId,
  userId,
  departmentIds,
  roleIds,
  projectIds,
) => {
  const payload = {
    departmentIds,
    roleIds,
    projectIds,
  };

  const response = await axios.post(
    `${Base_URL}/HrOrgDocumentVisibility/GetVisibleDocuments?orgId=${orgId}&userId=${userId}`,
    payload,
  );

  return response.data;
};

export const getResourcesByOrgId = async (orgId) => {
  const res = await axios.get(`${Base_URL}/Resource/GetByOrg/${orgId}`);
  return res.data;
};

export const getDocumentHistory = async (parentId) => {
  const res = await axios.get(
    `${Base_URL}/HrOrgDocumentMaster/GetHistory/${parentId}`,
  );
  return res.data;
};
